import { ReactNode, useState } from "react";
import { observer } from "mobx-react-lite";
import { useNavigate } from "react-router-dom";
import {
    createOrganisation,
    CreateOrganisationData,
    setActiveOrganisation,
} from "@/api";
import { appState } from "@/app/appState";
import { organisation_tree_node } from "@/app/typing/organisations";
import { Request } from "@/components/CRUD";
import { ActionHandler } from "@/components/UI/tools";
import { Card as CardIcon, Chart, Danger, Paper, User } from "@/components/UI/svgs";
import { canWrite } from "./tree";
import { BillingSection } from "./billingSection";
import { DangerSection } from "./dangerSection";
import { PeopleSection } from "./peopleSection";
import { nonePromptSet, PromptsSection, usePromptSets } from "./promptsSection";
import { Card, Field, Pill, SelectField, TextField } from "./ui";
import { UsageSection } from "./usage/usageSection";

export const ORG_TABS = ["billing", "prompts", "people", "usage", "danger"] as const;
export type OrgTab = (typeof ORG_TABS)[number];

export const isOrgTab = (value: string): value is OrgTab => (ORG_TABS as readonly string[]).includes(value);

const TAB_ICONS: Record<OrgTab, ReactNode> = {
    billing: <CardIcon size={14} />,
    prompts: <Paper size={14} />,
    people: <User size={14} />,
    usage: <Chart size={14} />,
    danger: <Danger size={14} />,
};

const SUPER_ADMIN_TABS: ReadonlySet<OrgTab> = new Set(["usage", "danger"]);

export const visibleOrgTabs = (): OrgTab[] =>
    ORG_TABS.filter((tab) => !SUPER_ADMIN_TABS.has(tab) || !!appState.user?.is_super_admin);

/** The create form takes the place of the inspector, under the row you clicked. */
export const NewOrgInspector = observer(({
    parent,
    onCreated,
    onCancel,
}: {
    parent: organisation_tree_node;
    onCreated: (orgId: number) => void;
    onCancel: () => void;
}) => {
    const [name, setName] = useState("");
    const [defaultSetId, setDefaultSetId] = useState<number | undefined>(
        parent.default_prompt_set_id ?? undefined,
    );
    const [loading, setLoading] = useState(false);
    const { assignable } = usePromptSets(parent.id);

    const create = Request<CreateOrganisationData, any>(
        createOrganisation,
        () => ({
            body: {
                name,
                parent_organisation_id: parent.id,
                default_prompt_set_id: defaultSetId,
            },
        }),
        setLoading,
        (data: any) => onCreated(data?.organisation_id),
        (error) =>
            appState.setModal({
                title: appState.t("common.error"),
                body: appState.t("settings.org.errorCreate"),
                comment: { name: appState.t("common.detail"), val: error.detail?.toString() ?? "" },
            }),
        () => name.trim().length > 0,
    );

    return (
        <div className="flex flex-col gap-2 w-full">
            <div className="inspector-header">
                <p className="eyebrow">{appState.t("settings.org.newUnder")}</p>
                <p className="h2">{appState.t("common.create")}</p>
            </div>

            <Card>
                <div className="row-form">
                    <Field label={appState.t("common.name")}>
                        <TextField value={name} onChange={setName} autoFocus placeholder={appState.t("settings.org.namePlaceholder")} />
                    </Field>
                    <Field label={appState.t("settings.org.defaultPromptSet")}>
                        <SelectField
                            value={defaultSetId}
                            onChange={setDefaultSetId}
                            options={[nonePromptSet(), ...assignable.map((set) => ({ id: set.id, name: set.name }))]}
                        />
                    </Field>
                    <div className="flex flex-row justify-end gap-[.25rem]">
                        <ActionHandler title={appState.t("common.cancel")} funq={onCancel} disabled={loading}>
                            {appState.t("common.cancel")}
                        </ActionHandler>
                        <ActionHandler
                            title={appState.t("common.create")}
                            funq={() => create()}
                            disabled={loading || name.trim().length === 0}
                        >
                            {appState.t("common.create")}
                        </ActionHandler>
                    </div>
                </div>
            </Card>
        </div>
    );
});

/** One organisation, one page per tab; the tab is part of the URL. */
export const OrgInspector = observer(({
    node,
    holder,
    tab,
    defaultUsage,
    onTab,
    onSelectOrg,
    onOpenPromptSet,
    onNewPromptSet,
    onRefresh,
    onDeactivated,
}: {
    node: organisation_tree_node;
    holder?: organisation_tree_node;
    tab: OrgTab;
    // How many reachable organisations use each prompt set as their default.
    defaultUsage: Map<number, number>;
    onTab: (tab: OrgTab) => void;
    onSelectOrg: (id: number) => void;
    onOpenPromptSet: (setId: number) => void;
    onNewPromptSet: () => void;
    onRefresh: () => void;
    onDeactivated: () => void;
}) => {
    const navigate = useNavigate();
    const writable = canWrite(node);
    const isActive = node.id === appState.user?.active_organisation?.id;
    const tabs = visibleOrgTabs();
    const current = tabs.includes(tab) ? tab : "billing";

    const workHere = async () => {
        const response = await setActiveOrganisation({ body: { id: node.id } });
        if (response.data) {
            appState.loadUser();
            navigate("/workspace");
        } else {
            appState.setModal({ title: appState.t("common.error"), body: appState.t("settings.org.errorSwitch") });
        }
    };

    const page = () => {
        switch (current) {
            case "billing":
                return (
                    <BillingSection
                        node={node}
                        holder={holder}
                        canEdit={writable}
                        onSelectOrg={onSelectOrg}
                        onChanged={onRefresh}
                    />
                );
            case "prompts":
                return (
                    <PromptsSection
                        node={node}
                        defaultUsage={defaultUsage}
                        onOpenPromptSet={onOpenPromptSet}
                        onNewPromptSet={onNewPromptSet}
                        onRefresh={onRefresh}
                    />
                );
            case "people":
                return <PeopleSection orgId={node.id} canManage={writable} />;
            case "usage":
                return <UsageSection orgId={node.id} />;
            case "danger":
                return <DangerSection node={node} onRefresh={onRefresh} onDeactivated={onDeactivated} />;
        }
    };

    return (
        <div className="flex flex-col gap-2 w-full">
            <div className="flex flex-row items-center justify-between gap-1 flex-wrap">
                <div role="tablist" className="tabs">
                    {tabs.map((t) => (
                        <button
                            key={t}
                            role="tab"
                            aria-selected={t === current}
                            className={`tab flex flex-row items-center gap-[.35rem] ${t === current ? "tab-active" : ""}`}
                            onClick={() => onTab(t)}
                        >
                            {TAB_ICONS[t]}
                            {appState.t(`settings.org.tab.${t}`)}
                        </button>
                    ))}
                </div>

                <div className="flex flex-row items-center gap-[.5rem]">
                    {isActive ? (
                        <Pill tone="accent">{appState.t("settings.org.currentWorkspace")}</Pill>
                    ) : (
                        <ActionHandler title={appState.t("settings.org.switchWorkspace")} funq={workHere} glow>
                            {appState.t("settings.org.workHere")}
                        </ActionHandler>
                    )}
                </div>
            </div>

            {page()}
        </div>
    );
});
